import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-1.5 font-medium tracking-tight whitespace-nowrap " +
		"transition-[background-color,color,border-color,transform,box-shadow] duration-150 " +
		"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40 " +
		"disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none " +
		"active:translate-y-px",
	{
		variants: {
			variant: {
				primary:
					"bg-gray-900 hover:bg-black active:bg-black text-white " +
					"dark:bg-white dark:hover:bg-gray-100 dark:active:bg-gray-200 dark:text-gray-900 " +
					"border border-gray-900/10 dark:border-white/10 " +
					"disabled:bg-gray-200 disabled:text-gray-400 disabled:border-gray-200 " +
					"dark:disabled:bg-white/[0.04] dark:disabled:text-gray-500",
				secondary:
					"bg-white hover:bg-gray-50 active:bg-gray-100 text-gray-800 " +
					"dark:bg-white/[0.04] dark:hover:bg-white/[0.07] dark:active:bg-white/[0.09] dark:text-gray-200 " +
					"border border-gray-200/80 dark:border-white/[0.08]",
				success:
					"bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white " +
					"border border-emerald-700/30 " +
					"shadow-[inset_0_1px_0_rgba(255,255,255,0.15)] " +
					"disabled:bg-gray-200 disabled:text-gray-400 disabled:border-gray-200 disabled:shadow-none " +
					"dark:disabled:bg-white/[0.04] dark:disabled:text-gray-500 dark:disabled:border-white/[0.06]",
				warning:
					"bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white " +
					"border border-orange-700/30 " +
					"shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]",
				warningOutline:
					"bg-orange-50 hover:bg-orange-100 active:bg-orange-200 text-orange-700 " +
					"dark:bg-orange-500/10 dark:hover:bg-orange-500/15 dark:active:bg-orange-500/20 dark:text-orange-300 " +
					"border border-orange-200 dark:border-orange-500/30",
				danger:
					"bg-red-600 hover:bg-red-700 active:bg-red-800 text-white " +
					"border border-red-700/30 " +
					"shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]",
				ghost:
					"text-gray-600 dark:text-gray-400 " +
					"hover:text-gray-900 dark:hover:text-white " +
					"hover:bg-gray-100 dark:hover:bg-white/[0.06] " +
					"border border-transparent",
			},
			size: {
				xs: "px-2 py-1 text-[11px] rounded-md",
				sm: "px-3 py-1.5 text-xs rounded-md",
				md: "px-3.5 py-2 text-[13px] rounded-md",
				lg: "px-4 py-2.5 text-sm rounded-md",
				icon: "w-7 h-7 rounded-md p-0 active:translate-y-0",
			},
		},
		defaultVariants: {
			variant: "secondary",
			size: "sm",
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, type = "button", ...props }, ref) => {
		return (
			<button
				ref={ref}
				type={type}
				className={cn(buttonVariants({ variant, size, className }))}
				{...props}
			/>
		);
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
